import { StoreController } from 'https://esm.sh/@nanostores/lit'
import { WithProfiler } from '@dna/mixins'
import styles from '@dna/styles'
import { LitElement, css, html, nothing, repeat, when } from '@lit'

class DNAProgressBar extends WithProfiler(LitElement) {
  static properties = {
    bannerId: {
      type: String,
      attribute: 'banner-id',
    },
    bannerText: {
      type: String,
      attribute: 'banner-text',
    },
  }

  constructor() {
    super()

    this.ctrl = null
    this.cfg = null
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {}

  willUpdate(changedProperties) {
    if (changedProperties.has('bannerId')) {
      this._setupHypeBanner(this.bannerId)
    }
  }

  _setupHypeBanner(id) {
    if (!window.HypeUI?.TieredCartController) return
    if (!this.ctrl || this.ctrl.identifier !== id) {
      this.ctrl = new window.HypeUI.TieredCartController(id)
      this.cfg = new StoreController(this, this.ctrl.config)
    }
  }

  get headerMessage() {
    const headerMessage = this.cfg?.value?.message
    if (!headerMessage.includes('$')) {
      return headerMessage
    }
    const allWords = headerMessage.split(' ')
    return allWords.map((word) => {
      if (word.includes('$')) {
        return html`<span class="red">${word}</span> `
      }
      return html`${word} `
    })
  }

  get sortedTiers() {
    const allTiers = this.cfg?.value?.allTiers || []
    return allTiers.sort((a, b) => a.minAmount - b.minAmount)
  }

  get progressBarWidth() {
    const config = this.cfg?.value
    const cartTotal = config?.cartTotal || 0
    const allTiers = this.sortedTiers

    // If there are no tiers or the cart is empty, progress bar is 0%
    if (allTiers.length === 0 || cartTotal === 0) {
      return 0
    }

    // Set the current min amount to be the min of the first tier
    let currentMin = allTiers[0].minAmount
    let prevMin = 0
    let totalTiersCompleted = 0

    // Go through each tier to find which tier the cart total is in
    for (let i = 0; i < allTiers.length; i++) {
      const currentTier = allTiers[i]
      // If cart total is less than the current tier, we are at the current tier
      if (cartTotal < currentTier.minAmount) {
        currentMin = currentTier.minAmount
        break
      }
      // Otherwise, this tier has been completed
      prevMin = currentTier.minAmount
      totalTiersCompleted = totalTiersCompleted + 1
    }

    // If all tiers have been completed, progress bar is 100%
    if (totalTiersCompleted === allTiers.length) {
      return 100
    }

    // Calculate progress within current tier and then add to the total tiers completed
    const progressInCurrentTier = (cartTotal - prevMin) / (currentMin - prevMin)
    const progressBar = totalTiersCompleted + progressInCurrentTier

    // Convert progress bar %
    return (progressBar / allTiers.length) * 100
  }

  get dividerPositions() {
    // we want to calculate where the 'divider' lines should appear based on # of tiers
    // if there are 1 or less tiers, don't show divider lines
    const allTiers = this.sortedTiers
    const tierPositions = []
    if (allTiers.length <= 1) {
      return tierPositions
    }
    for (let i = 1; i < allTiers.length; i++) {
      tierPositions.push((i / allTiers.length) * 100)
    }
    return tierPositions
  }
  render() {
    const config = this.cfg?.value
    return html`
      ${when(
        config && this.sortedTiers?.length > 0,
        () => html`
        <div class="progress-bar">
          <div class="progress-bar__headers">
            ${when(
              config.cartTotal > 0,
              () =>
                html`<div class="progress-bar__header">${this.headerMessage}</div>`,
              () => nothing,
            )}
            ${when(
              this.bannerText,
              () =>
                html`<div class="progress-bar__subheader">${this.bannerText}</div>`,
              () => nothing,
            )}
          </div>
          <div class="progress-bar__container">
            <div class="progress-bar__body">
              <div class="progress-bar__fill" style="width: ${this.progressBarWidth}%">
              </div>
              ${repeat(
                this.dividerPositions || [],
                (position) => position,
                (position) => html`
                  <div class="progress-bar__divider ${this.progressBarWidth > position ? 'completed' : ''}" style="left: ${position}%;">
                  </div>
                `,
              )}
            </div>
            ${when(
              this.sortedTiers.length > 1,
              () => html`
              <div class="progress-bar__messages">
                ${repeat(
                  this.sortedTiers || [],
                  (tier) => tier.index,
                  (tier) => html`
                    <div>${tier.label}</div>
                  `,
                )}
                </div>`,
              () => nothing,
            )}
          </div>
        </div>
      `,
        () => nothing,
      )}
  `
  }

  static styles = [
    styles.a11y,
    styles.flex,
    styles.typography,
    styles.shoelace,
    css`
    .progress-bar {
      margin-bottom: var(--dna-space-4);
    }
    .progress-bar__headers {
      text-align: center;
      margin-bottom: var(--dna-space-4);
      line-height: 24px;
    }
    .progress-bar__header {
      font-size: 16px;
    }
    .progress-bar__header .red {
      color: var(--dna-utility-attention);
    }
    .progress-bar__subheader {
      font-size: 14px;
      font-family: var(--dna-font-bebas);
      letter-spacing: 0;
      line-height: 14px;
    }
    .progress-bar__body {
      height: 8px;
      background-color: #F4F2EF;
      position: relative;
      margin-left: 16px;
      margin-right: 16px;
    }
    .progress-bar__divider {
      position: absolute;
      top: 0;
      height: 100%;
      width: 2px;
      background: var(--dna-utility-disabled);
      z-index: 2;
      transition: background .8s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .progress-bar__divider.completed {
      background: var(--dna-brand-reverse);
    }
    .progress-bar__fill {
      background-color: var(--dna-color-midnight);
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      transition: width .8s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .progress-bar__messages {
      display: flex;
      justify-content: space-around;
      margin-left: 16px;
      margin-right: 16px;
      margin-top: 6px;
      font-size: 15px;
      line-height: 18px;
      font-family: var(--dna-font-bebas);
      letter-spacing: 0;
    }
    `,
  ]
}

customElements.define('dna-progress-bar', DNAProgressBar)
